"use client";
import { publicErrorMessage } from "@ocr-monorepo/shared";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@ocr-monorepo/ui/components/button";
import { TurnstileWidget } from "@/components/turnstile-widget";
import { client as authClient, useSession } from "@/lib/auth-client";
import { useTRPC } from "@/lib/trpc";
import posthog from "posthog-js";

export default function AnonymousPdfToCsvPage() {
  const router = useRouter();
  const trpc = useTRPC();
  const { data: session, isPending: sessionPending, refetch } = useSession();
  const [authError, setAuthError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const isAnonymous =
    (session?.user as { isAnonymous?: boolean } | undefined)?.isAnonymous ===
    true;

  useEffect(() => {
    if (sessionPending || session) return;
    void authClient.signIn.anonymous().then(async (result) => {
      if (result.error) {
        posthog.capture("anonymous_sign_in_failed", {
          error_code: "system_error",
        });
        setAuthError(publicErrorMessage("system_error"));
        return;
      }
      await refetch();
    });
  }, [refetch, session, sessionPending]);

  const createConversion = useMutation(
    trpc.converters.create.mutationOptions({
      onSuccess: ({ conversion }) => {
        router.push(`/dashboard/convert/pdf-to-csv?conversion=${conversion.id}`);
      },
      onError: () => setAuthError(publicErrorMessage("system_error")),
    }),
  );

  const canContinue = !!session && (!isAnonymous || !!turnstileToken);

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-16">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <span className="mx-auto flex size-12 items-center justify-center text-primary">
            <FileSpreadsheet className="size-7" />
          </span>
          <h1 className="mt-4 text-2xl font-semibold tracking-tight">Convert PDF invoices to CSV</h1>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Start a temporary conversion, review extracted rows beside the source PDF, and download a clean CSV.
          </p>
        </div>

        {sessionPending || (!session && !authError) ? (
          <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Preparing a secure session…
          </div>
        ) : (
          <div className="space-y-5">
            {isAnonymous && (
              <TurnstileWidget
                action="pdf-to-csv"
                onSuccess={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setAuthError("Bot verification failed. Please try again.")}
              />
            )}
            {authError && <p className="text-center text-sm text-destructive">{authError}</p>}
            <Button
              className="w-full"
              size="lg"
              disabled={!canContinue || createConversion.isPending}
              onClick={() => createConversion.mutate({
                type: "pdf_to_csv",
                turnstileToken: isAnonymous ? turnstileToken ?? undefined : undefined,
              })}
            >
              {createConversion.isPending ? <Loader2 className="mr-2 size-4 animate-spin" /> : null}
              Start converting
            </Button>
            <dl className="grid grid-cols-3 divide-x divide-border border-y border-border py-3 text-center">
              <div className="px-2"><dt className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Files</dt><dd className="mt-1 text-xs font-medium">Up to 3 PDFs</dd></div>
              <div className="px-2"><dt className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Pages</dt><dd className="mt-1 text-xs font-medium">5 total</dd></div>
              <div className="px-2"><dt className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Expires</dt><dd className="mt-1 text-xs font-medium">After 2 hours</dd></div>
            </dl>
            <p className="text-center text-[11px] leading-relaxed text-muted-foreground">
              The five-page allowance is shared across every PDF in this temporary session.
            </p>
          </div>
        )}
      </div>
    </main>
  );
}
